import * as Sentry from '@sentry/react';
import './index.css';
import { ROUTES } from './lib/routes';
import { SystemLayout } from './routes/app/app-layout';
import CatalogPage from './routes/app/settings/catalog-settings';
import FinancePage from './routes/app/settings/finance-settings';
import MarketingPage from './routes/app/settings/marketing-settings';
import AuthLayout from '../auth-layout';
import { Suspense, lazy } from 'react';
import { Navigate, createBrowserRouter } from 'react-router';
import SaleCreatePage from './routes/app/finances/create-sale';
import { SalesRouter } from './routes/app/finances/finance-router';
import SaleDetailPage from './routes/app/finances/sale-[id]';
import { BrandingRouter } from './routes/app/settings/router-branding-settings';
import GlobalErrorBoundary from './routes/error-boundary';

// Lazy load pages
const LoginPage = lazy(() => import('./routes/login/login'));
const CampaignDashboard = lazy(
  () => import('./routes/app/marketing/campaign-dashboard'),
);
const MessengerPage = lazy(
  () => import('./routes/app/marketing/messenger-page'),
);
const ChatDetailPage = lazy(
  () => import('./routes/app/marketing/chat-detail-page'),
);
const HomePage = lazy(() => import('./routes/app/home'));
const ClientsPage = lazy(() => import('./routes/app/clients/clients-page'));
const ClientDetailPage = lazy(() => import('./routes/app/clients/client-[id]'));
const ProfilePage = lazy(() => import('./routes/app/profile/profile-page'));
const AnalyticsPage = lazy(() => import('./routes/app/profile/analytics-page'));
const ProductDetailPage = lazy(
  () => import('./routes/app/products/product-[id]'),
);
const CategoryDetailPage = lazy(
  () => import('./routes/app/products/category-[id]'),
);
const ProductsPage = lazy(() => import('./routes/app/products/products-page'));
const ExpensesPage = lazy(() => import('./routes/app/finances/expenses-page'));
const TrashPage = lazy(() => import('./routes/app/settings/trash-page'));
const AcademyPage = lazy(() => import('./routes/app/academy/academy-page'));

const AcademySettingsPage = lazy(() => import('./routes/app/academy/academy-settings-page'));
const CourseEditorPage = lazy(() => import('./routes/app/academy/course-editor'));
const SectionsSettingsPage = lazy(() => import('./routes/app/settings/sections-settings'));

// Loading fallback
function LoadingFallback() {
  return (
    <div className="bg-background flex h-screen items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="border-primary h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    </div>
  );
}

export const router = createBrowserRouter([
  // Auth routes
  {
    errorElement: <GlobalErrorBoundary />,
    element: <AuthLayout />,
    children: [
      {
        path: ROUTES.PAGE.LOGIN,
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <LoginPage />
          </Suspense>
        ),
      },
      {
        path: '/app',
        element: (
          <Sentry.ErrorBoundary
            fallback={(errorProps) => <GlobalErrorBoundary {...errorProps} />}
          >
            <SystemLayout />
          </Sentry.ErrorBoundary>
        ),
        children: [
          // Home / Principal (CRM Leads)
          {
            path: ROUTES.DASHBOARD.PRINCIPAL,
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <CampaignDashboard />
              </Suspense>
            ),
          },

          // Finance routes
          {
            path: ROUTES.FINANCES.ROOT,
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <SalesRouter />
              </Suspense>
            ),
          },
          {
            path: ROUTES.SALES.DETAIL(':id'),
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <SaleDetailPage />
              </Suspense>
            ),
          },
          {
            path: ROUTES.FINANCES.EXPENSES,
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <ExpensesPage />
              </Suspense>
            ),
          },
          {
            path: ROUTES.SALES.CREATE,
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <SaleCreatePage />
              </Suspense>
            ),
          },

          // Comunidad routes (Unified Clients & Students)
          {
            path: '/app/clientes',
            element: <Navigate to={ROUTES.CLIENTS.ROOT} replace />,
          },
          {
            path: ROUTES.CLIENTS.ROOT,
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <ClientsPage />
              </Suspense>
            ),
          },
          {
            path: ROUTES.CLIENTS.DETAIL(':id'),
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <ClientDetailPage />
              </Suspense>
            ),
          },

          // --- ACADEMY ---
          {
            path: ROUTES.ACADEMY.ROOT,
            children: [
              {
                index: true,
                element: <Navigate to={ROUTES.ACADEMY.COURSES} replace />,
              },
              {
                path: 'cursos',
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <AcademyPage />
                  </Suspense>
                ),
              },
              {
                path: 'estudiantes',
                element: <Navigate to={ROUTES.ACADEMY.STUDENTS} replace />,
              },
              {
                path: 'ajustes',
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <AcademySettingsPage />
                  </Suspense>
                ),
              },
              {
                path: 'cursos/:id',
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <CourseEditorPage />
                  </Suspense>
                ),
              },
            ],
          },

          // UNIFIED CATALOG
          {
            path: ROUTES.CATALOG.ROOT,
            children: [
              {
                index: true,
                element: <Navigate to={ROUTES.CATALOG.PRODUCTS} replace />,
              },
              {
                path: 'productos',
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <ProductsPage />
                  </Suspense>
                ),
              },
            ],
          },

          // Individual item details
          {
            path: ROUTES.PRODUCTS.DETAIL(':id'),
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <ProductDetailPage />
              </Suspense>
            ),
          },
          {
            path: ROUTES.CATEGORY.DETAIL(':id'),
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <CategoryDetailPage />
              </Suspense>
            ),
          },

          // Profile & Analytics
          {
            path: ROUTES.PROFILE.ROOT,
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <ProfilePage />
              </Suspense>
            ),
          },
          {
            path: ROUTES.PROFILE.ANALYTICS,
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <AnalyticsPage />
              </Suspense>
            ),
          },
          {
            path: ROUTES.PROFILE.CAMPAIGNS,
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <CampaignDashboard />
              </Suspense>
            ),
          },
          {
            path: ROUTES.PROFILE.MESSENGER,
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <MessengerPage />
              </Suspense>
            ),
          },
          {
            path: ROUTES.PROFILE.MESSENGER_CHAT(':phone'),
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <ChatDetailPage />
              </Suspense>
            ),
          },

          // Settings routes
          {
            path: ROUTES.SETTINGS.ROOT,
            element: <Navigate to={ROUTES.PROFILE.ROOT} replace />,
          },
          {
            path: ROUTES.SETTINGS.BRANDING,
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <BrandingRouter />
              </Suspense>
            ),
          },
          {
            path: ROUTES.SETTINGS.FINANCE,
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <FinancePage />
              </Suspense>
            ),
          },
          {
            path: ROUTES.SETTINGS.CATALOG,
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <CatalogPage />
              </Suspense>
            ),
          },
          {
            path: ROUTES.SETTINGS.MARKETING,
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <MarketingPage />
              </Suspense>
            ),
          },
          {
            path: ROUTES.SETTINGS.TRASH,
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <TrashPage />
              </Suspense>
            ),
          },
          {
            path: ROUTES.SETTINGS.SECTIONS,
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <SectionsSettingsPage />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to={ROUTES.DASHBOARD.PRINCIPAL} replace />,
  },
]);
